/**
 * Sector Operations — Delivery Change Requests, Client Approvers & UAT Service (R39 E2.a + E2.b, SO-P269).
 * Typed reads, subscriptions, and callable wrappers for change requests, UAT checklists, and rounds.
 */

import {
  collection,
  doc,
  onSnapshot,
  orderBy,
  query,
  where,
  type Unsubscribe,
} from 'firebase/firestore'
import { firestoreDb } from './firebase'
import { callable } from './callable'
import type {
  OpsEngagementContacts,
  OpsChangeRequest,
  OpsChangeRequestFinance,
  OpsChangeRequestVersion,
  OpsUatChecklist,
  OpsUatChecklistVersion,
  OpsUatRound,
  SetEngagementClientApproversInput,
  CreateChangeRequestInput,
  UpdateChangeRequestInput,
  SetChangeRequestPriceInput,
  SendChangeRequestInput,
  WithdrawChangeRequestInput,
  UpsertUatChecklistInput,
  StartUatRoundInput,
  SetUatResultInput,
  SendUatSignoffInput,
  CloseUatRoundInput,
} from '../types/ops'

// ---------------------------------------------------------------------------
// Callables
// ---------------------------------------------------------------------------

export async function setEngagementClientApprovers(
  input: SetEngagementClientApproversInput,
): Promise<{ success: boolean; engagementId: string; contacts: OpsEngagementContacts; opId?: string }> {
  return callable<SetEngagementClientApproversInput, { success: boolean; engagementId: string; contacts: OpsEngagementContacts; opId?: string }>(
    'setEngagementClientApprovers',
    input,
  )
}

export async function createChangeRequest(
  input: CreateChangeRequestInput,
): Promise<{ success: boolean; crId: string; number: number; numberFormatted: string; opId?: string; replayed?: boolean }> {
  return callable<CreateChangeRequestInput, { success: boolean; crId: string; number: number; numberFormatted: string; opId?: string; replayed?: boolean }>(
    'createChangeRequest',
    input,
  )
}

export async function updateChangeRequest(
  input: UpdateChangeRequestInput,
): Promise<{ success: boolean; crId: string; opId?: string; replayed?: boolean }> {
  return callable<UpdateChangeRequestInput, { success: boolean; crId: string; opId?: string; replayed?: boolean }>(
    'updateChangeRequest',
    input,
  )
}

export async function setChangeRequestPrice(
  input: SetChangeRequestPriceInput,
): Promise<{ success: boolean; crId: string; opId?: string; replayed?: boolean }> {
  return callable<SetChangeRequestPriceInput, { success: boolean; crId: string; opId?: string; replayed?: boolean }>(
    'setChangeRequestPrice',
    input,
  )
}

export async function sendChangeRequest(
  input: SendChangeRequestInput,
): Promise<{ success: boolean; crId: string; approvalId: string; version: number; opId?: string; replayed?: boolean }> {
  return callable<SendChangeRequestInput, { success: boolean; crId: string; approvalId: string; version: number; opId?: string; replayed?: boolean }>(
    'sendChangeRequest',
    input,
  )
}

export async function withdrawChangeRequest(
  input: WithdrawChangeRequestInput,
): Promise<{ success: boolean; crId: string; opId?: string; replayed?: boolean }> {
  return callable<WithdrawChangeRequestInput, { success: boolean; crId: string; opId?: string; replayed?: boolean }>(
    'withdrawChangeRequest',
    input,
  )
}

export async function upsertUatChecklist(
  input: UpsertUatChecklistInput,
): Promise<{ success: boolean; checklistId: string; version: number; opId?: string; replayed?: boolean }> {
  return callable<UpsertUatChecklistInput, { success: boolean; checklistId: string; version: number; opId?: string; replayed?: boolean }>(
    'upsertUatChecklist',
    input,
  )
}

export async function startUatRound(
  input: StartUatRoundInput,
): Promise<{ success: boolean; roundId: string; roundNumber: number; opId?: string; replayed?: boolean }> {
  return callable<StartUatRoundInput, { success: boolean; roundId: string; roundNumber: number; opId?: string; replayed?: boolean }>(
    'startUatRound',
    input,
  )
}

export async function setUatResult(
  input: SetUatResultInput,
): Promise<{ success: boolean; roundId: string; itemId: string; opId?: string; replayed?: boolean }> {
  return callable<SetUatResultInput, { success: boolean; roundId: string; itemId: string; opId?: string; replayed?: boolean }>(
    'setUatResult',
    input,
  )
}

export async function sendUatSignoff(
  input: SendUatSignoffInput,
): Promise<{ success: boolean; roundId: string; approvalId: string; opId?: string; replayed?: boolean }> {
  return callable<SendUatSignoffInput, { success: boolean; roundId: string; approvalId: string; opId?: string; replayed?: boolean }>(
    'sendUatSignoff',
    input,
  )
}

export async function closeUatRound(
  input: CloseUatRoundInput,
): Promise<{ success: boolean; roundId: string; opId?: string; replayed?: boolean }> {
  return callable<CloseUatRoundInput, { success: boolean; roundId: string; opId?: string; replayed?: boolean }>(
    'closeUatRound',
    input,
  )
}

// ---------------------------------------------------------------------------
// Subscriptions
// ---------------------------------------------------------------------------

export function subscribeEngagementContacts(
  engagementId: string,
  onUpdate: (contacts: OpsEngagementContacts | null) => void,
  onError?: (err: Error) => void,
): Unsubscribe {
  if (!engagementId) {
    onUpdate(null)
    return () => {}
  }

  const ref = doc(firestoreDb, 'ops_engagement_contacts', engagementId)
  return onSnapshot(
    ref,
    (snap) => {
      if (!snap.exists()) {
        onUpdate(null)
        return
      }
      onUpdate({
        engagementId: snap.id,
        ...(snap.data() as Omit<OpsEngagementContacts, 'engagementId'>),
      })
    },
    (err) => {
      if (onError) onError(err)
    },
  )
}

export function subscribeChangeRequests(
  workspaceId: string,
  engagementId: string | null | undefined,
  onUpdate: (crs: OpsChangeRequest[]) => void,
  onError?: (err: Error) => void,
): Unsubscribe {
  if (!workspaceId) {
    onUpdate([])
    return () => {}
  }

  const coll = collection(firestoreDb, 'ops_change_requests')
  const q = engagementId
    ? query(
        coll,
        where('workspaceId', '==', workspaceId),
        where('engagementId', '==', engagementId),
        orderBy('createdAt', 'desc'),
      )
    : query(coll, where('workspaceId', '==', workspaceId), orderBy('createdAt', 'desc'))

  return onSnapshot(
    q,
    (snap) => {
      const crs: OpsChangeRequest[] = snap.docs.map((d) => ({
        id: d.id,
        ...(d.data() as Omit<OpsChangeRequest, 'id'>),
      }))
      onUpdate(crs)
    },
    (err) => {
      if (onError) onError(err)
    },
  )
}

export function subscribeChangeRequestVersions(
  crId: string,
  onUpdate: (versions: OpsChangeRequestVersion[]) => void,
  onError?: (err: Error) => void,
): Unsubscribe {
  if (!crId) {
    onUpdate([])
    return () => {}
  }

  const coll = collection(firestoreDb, 'ops_change_requests', crId, 'versions')
  const q = query(coll, orderBy('version', 'desc'))

  return onSnapshot(
    q,
    (snap) => {
      const versions: OpsChangeRequestVersion[] = snap.docs.map(
        (d) => d.data() as OpsChangeRequestVersion,
      )
      onUpdate(versions)
    },
    (err) => {
      if (onError) onError(err)
    },
  )
}

export function subscribeChangeRequestFinance(
  crId: string,
  onUpdate: (finance: OpsChangeRequestFinance | null) => void,
  onError?: (err: Error) => void,
): Unsubscribe {
  if (!crId) {
    onUpdate(null)
    return () => {}
  }

  const ref = doc(firestoreDb, 'ops_change_request_finance', crId)
  return onSnapshot(
    ref,
    (snap) => {
      if (!snap.exists()) {
        onUpdate(null)
        return
      }
      onUpdate(snap.data() as OpsChangeRequestFinance)
    },
    (err) => {
      if (onError) onError(err)
    },
  )
}

export function subscribeUatChecklists(
  workspaceId: string,
  engagementId: string | null | undefined,
  onUpdate: (checklists: OpsUatChecklist[]) => void,
  onError?: (err: Error) => void,
): Unsubscribe {
  if (!workspaceId) {
    onUpdate([])
    return () => {}
  }

  const coll = collection(firestoreDb, 'ops_uat_checklists')
  const q = engagementId
    ? query(
        coll,
        where('workspaceId', '==', workspaceId),
        where('engagementId', '==', engagementId),
        orderBy('createdAt', 'desc'),
      )
    : query(coll, where('workspaceId', '==', workspaceId), orderBy('createdAt', 'desc'))

  return onSnapshot(
    q,
    (snap) => {
      const checklists: OpsUatChecklist[] = snap.docs.map((d) => ({
        id: d.id,
        ...(d.data() as Omit<OpsUatChecklist, 'id'>),
      }))
      onUpdate(checklists)
    },
    (err) => {
      if (onError) onError(err)
    },
  )
}

export function subscribeUatChecklistVersions(
  checklistId: string,
  onUpdate: (versions: OpsUatChecklistVersion[]) => void,
  onError?: (err: Error) => void,
): Unsubscribe {
  if (!checklistId) {
    onUpdate([])
    return () => {}
  }

  const coll = collection(firestoreDb, 'ops_uat_checklists', checklistId, 'versions')
  const q = query(coll, orderBy('version', 'desc'))

  return onSnapshot(
    q,
    (snap) => {
      const versions: OpsUatChecklistVersion[] = snap.docs.map(
        (d) => d.data() as OpsUatChecklistVersion,
      )
      onUpdate(versions)
    },
    (err) => {
      if (onError) onError(err)
    },
  )
}

export function subscribeUatRounds(
  workspaceId: string,
  engagementId: string | null | undefined,
  onUpdate: (rounds: OpsUatRound[]) => void,
  onError?: (err: Error) => void,
): Unsubscribe {
  if (!workspaceId) {
    onUpdate([])
    return () => {}
  }

  const coll = collection(firestoreDb, 'ops_uat_rounds')
  const q = engagementId
    ? query(
        coll,
        where('workspaceId', '==', workspaceId),
        where('engagementId', '==', engagementId),
        orderBy('createdAt', 'desc'),
      )
    : query(coll, where('workspaceId', '==', workspaceId), orderBy('createdAt', 'desc'))

  return onSnapshot(
    q,
    (snap) => {
      const rounds: OpsUatRound[] = snap.docs.map((d) => ({
        id: d.id,
        ...(d.data() as Omit<OpsUatRound, 'id'>),
      }))
      onUpdate(rounds)
    },
    (err) => {
      if (onError) onError(err)
    },
  )
}
